import {Route, Routes, useNavigate, useLocation} from 'react-router-dom';
import {Suspense, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import Loader from '@teladoc/pulse/ui/Loader';
import Arg from '@livongo/arg';
import {useFeatureFlagContext} from '@livongo/utilities/system/featureFlag';
import StorageUtils from '@livongo/utilities/system/storage';
import {FEATURE_FLAG_NAMES, IS_PROD} from './config';
import AmpliUtils from './utils/amplitude-utils';
import {TRACK_EVENT_KEYS} from './constants/amplitude';
import CommonUtils from './utils/common-utils';
import SchedulingPage from './SchedulingPage/SchedulingPage';
import SchedulingFormV2 from './SchedulingForm/SchedulingFormV2';
import ConfirmationPage from './ConfirmationPage/ConfirmationPage';
import ConfirmationPageV2 from './ConfirmationPage/ConfirmationPageV2';
import SessionDetails from './sessionDetails/SessionDetails';
import ConfirmationPageMentalHealth from './ConfirmationPage/ConfirmationPageMentalHealth';
import UserInfoAPI from './UserInfo/userInfo-api';
import FooterPublic from './Footer/Footer';
import Layout from './Layout/Layout';

const App = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const {updateFeatureFlag, featureFlags} = useFeatureFlagContext();
    const referrer = Arg('referrer');
    const isOneApp = Arg('oneapp') || Arg('oneAppSdk');
    const showFooter =
        !referrer || (referrer && !referrer.includes('mobile') && !isOneApp);
    const isMobile = Arg('mobile') === true;

    // Track page views on route changes - simplified
    useEffect(() => {
        // Simple page view tracking based on route
        const pageViewEvents = {
            '/confirmation': TRACK_EVENT_KEYS.CONFIRMATION_PAGE_VIEWED,
            '/v2/confirmation': TRACK_EVENT_KEYS.CONFIRMATION_PAGE_V2_VIEWED,
            '/confirmationMentalHealth':
                TRACK_EVENT_KEYS.CONFIRMATION_MENTAL_HEALTH_VIEWED,
            '/SessionDetails': TRACK_EVENT_KEYS.SESSION_DETAILS_VIEWED,
        };

        const eventKey = pageViewEvents[location.pathname];

        if (eventKey) {
            AmpliUtils.setTrackingData(eventKey, {
                referrer: referrer || 'direct',
                isOneApp,
                // eslint-disable-next-line camelcase
                is_mobile: isMobile,
            });
        }
    }, [location.pathname, referrer, isOneApp, isMobile]);

    useEffect(() => {
        const uuidArg = Arg('u');

        if (
            window.location.hostname.includes('teladoc') ||
            Arg('ccmOneApp') ||
            isOneApp
        ) {
            document.title = 'Teladoc Health - CCM Coach Request';
        }

        if (uuidArg) {
            UserInfoAPI.getAuthToken(uuidArg).then(({data}) => {
                dispatch({
                    type: 'AUTH',
                    payload: {
                        uuid: uuidArg,
                        token: data,
                    },
                });

                UserInfoAPI.schedulingData(data, uuidArg)
                    .then(userData => {
                        dispatch({
                            type: 'USER_DATA',
                            payload: userData,
                        });
                    })
                    .catch(({userData}) => {
                        dispatch({
                            type: 'ERROR',
                            payload: JSON.stringify(userData),
                        });
                        dispatch(UserInfoAPI.incorrectToken);
                    });

                // Identify user in Amplitude with session_start event
                // Try to get account data to determine migration status
                UserInfoAPI.getInternalUserId(data, uuidArg)
                    .then(accountData => {
                        // User has completed migration - has Teladoc member_id
                        AmpliUtils.identifyUser(uuidArg, accountData);
                        AmpliUtils.setTrackingData(
                            TRACK_EVENT_KEYS.SESSION_START,
                            {
                                referrer: referrer || 'direct',
                                isOneApp,
                                // eslint-disable-next-line camelcase
                                is_mobile: isMobile,
                            }
                        );

                        dispatch({
                            type: 'USER_DATA',
                            payload: {internalUserId: accountData?.uuid},
                        });
                    })
                    .catch(() => {
                        // User has not completed migration - use Livongo UUID
                        AmpliUtils.identifyUser(uuidArg);
                        AmpliUtils.setTrackingData(
                            TRACK_EVENT_KEYS.SESSION_START,
                            {
                                referrer: referrer || 'direct',
                                isOneApp,
                                // eslint-disable-next-line camelcase
                                is_mobile: isMobile,
                            }
                        );
                    });
            });
        } else {
            const accessToken = StorageUtils.get({
                key: 'access_token',
                type: 'cookie',
                useNative: true,
                parse: false,
            });
            const uuid = accessToken
                ? JSON.parse(atob(accessToken.split('.')[1])).sub.replace(
                      'user/liv/',
                      ''
                  )
                : '';

            if (accessToken && uuid) {
                dispatch({
                    type: 'AUTH',
                    payload: {
                        token: accessToken,
                        uuid,
                    },
                });

                UserInfoAPI.schedulingData(accessToken, uuid)
                    .then(data => {
                        dispatch({
                            type: 'USER_DATA',
                            payload: data,
                        });
                    })
                    .catch(({data}) => {
                        dispatch({
                            type: 'ERROR',
                            payload: JSON.stringify(data),
                        });
                        dispatch(UserInfoAPI.incorrectToken);
                    });

                // Identify user in Amplitude with session_start event
                // Try to get account data to determine migration status
                UserInfoAPI.getInternalUserId(accessToken, uuid)
                    .then(accountData => {
                        // User has completed migration - has Teladoc member_id
                        AmpliUtils.identifyUser(uuid, accountData);
                        AmpliUtils.setTrackingData(
                            TRACK_EVENT_KEYS.SESSION_START,
                            {
                                referrer: referrer || 'direct',
                                isOneApp,
                                // eslint-disable-next-line camelcase
                                is_mobile: isMobile,
                            }
                        );
                    })
                    .catch(() => {
                        // User has not completed migration - use Livongo UUID
                        AmpliUtils.identifyUser(uuid);
                        AmpliUtils.setTrackingData(
                            TRACK_EVENT_KEYS.SESSION_START,
                            {
                                referrer: referrer || 'direct',
                                isOneApp,
                                // eslint-disable-next-line camelcase
                                is_mobile: isMobile,
                            }
                        );
                    });
            } else {
                navigate('/');
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isMobile) {
            dispatch({
                type: 'VIEW',
                payload: {
                    enableNewUi: true,
                    view: 'smallview',
                    calendarId: Arg('calendarId'),
                    mobile: true,
                },
            });
        } else {
            dispatch({
                type: 'VIEW',
                payload: {
                    enableNewUi: true,
                    view: 'fullview',
                    calendarId: Arg('calendarId'),
                    mobile: false,
                },
            });
        }
    }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const axleFormEnabled = CommonUtils.isFeatureEnabled(
            featureFlags,
            FEATURE_FLAG_NAMES.enableAxleForm,
            isOneApp
        );

        dispatch({
            type: 'SET_FEATURE_FLAGS',
            payload: {
                enableAxleForm: axleFormEnabled,
            },
        });
    }, [featureFlags, isOneApp, dispatch]);

    // This window object is used to update FeatureFlag Toggle via console.
    if (!IS_PROD) {
        window.updateFeatureFlag = ({
            featureName,
            oneAppActive = false,
            legacyActive = false,
        }) => {
            return updateFeatureFlag({
                featureName,
                oneAppActive,
                legacyActive,
            });
        };
    }

    return (
        <Suspense fallback={<Loader />}>
            <Routes>
                <Route element={<Layout />}>
                    <Route path="/" element={<SchedulingPage />} />
                    <Route path="/v2" element={<SchedulingPage />} />
                    <Route path="/v2/schedule" element={<SchedulingFormV2 />} />
                    <Route
                        path="/confirmation"
                        element={<ConfirmationPage />}
                    />
                    <Route
                        path="/v2/confirmation"
                        element={<ConfirmationPageV2 />}
                    />
                    <Route
                        path="/confirmationMentalHealth"
                        element={<ConfirmationPageMentalHealth />}
                    />
                    <Route
                        path="/SessionDetails"
                        element={<SessionDetails />}
                    />
                </Route>
            </Routes>
            {!isMobile && showFooter && (
                <div>
                    <FooterPublic />
                </div>
            )}
        </Suspense>
    );
};

export default App;
